/**
 * Blackjack Interactive Component
 *
 * Displays player cards and dealer card, lets user choose an action.
 */

import { useState } from 'react';
import { BlackjackHand, type BlackjackConfig } from '../PlayingCards';
import styles from './Blackjack.module.css';

interface BlackjackProps {
  config: BlackjackConfig;
  onSubmit: (answer: string) => void;
  disabled: boolean;
  showResult: boolean;
  correctAnswer: string;
}

export function Blackjack({ config, onSubmit, disabled, showResult, correctAnswer }: BlackjackProps) {
  const [selectedAction, setSelectedAction] = useState<string | null>(null);

  // Determine which actions to show based on hand type
  const isPair = config.handType === 'pair';
  const actions = isPair
    ? ['Hit', 'Stand', 'Double Down', 'Split']
    : ['Hit', 'Stand', 'Double Down'];

  const handleAction = (action: string) => {
    if (disabled) return;
    setSelectedAction(action);
    onSubmit(action);
  };

  const getButtonClass = (action: string) => {
    let className = styles.actionButton;

    if (showResult) {
      if (action === correctAnswer) {
        className += ` ${styles.correct}`;
      } else if (action === selectedAction && action !== correctAnswer) {
        className += ` ${styles.incorrect}`;
      }
    } else if (action === selectedAction) {
      className += ` ${styles.selected}`;
    }

    return className;
  };

  // Action icons
  const getActionIcon = (action: string) => {
    switch (action) {
      case 'Hit':
        return (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
        );
      case 'Stand':
        return (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14" />
          </svg>
        );
      case 'Double Down':
        return (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 8l7 4 7-4" />
          </svg>
        );
      case 'Split':
        return (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3v18M16 3v18M3 12h18" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.container}>
      {/* Card display */}
      <BlackjackHand config={config} />

      {/* Action buttons */}
      <div className={styles.actions}>
        {actions.map((action) => (
          <button
            key={action}
            className={getButtonClass(action)}
            onClick={() => handleAction(action)}
            disabled={disabled}
          >
            <span className={styles.actionIcon}>{getActionIcon(action)}</span>
            <span className={styles.actionLabel}>{action}</span>
          </button>
        ))}
      </div>

      {/* Result feedback */}
      {showResult && selectedAction && (
        <div className={`${styles.feedback} ${selectedAction === correctAnswer ? styles.feedbackCorrect : styles.feedbackIncorrect}`}>
          {selectedAction === correctAnswer ? (
            <>Correct! {correctAnswer} is the right play.</>
          ) : (
            <>The correct play is <strong>{correctAnswer}</strong>.</>
          )}
        </div>
      )}
    </div>
  );
}
